import React from 'react';
import { Link } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import {
  StyledPageWrapper,
  StyledHeader,
  StyledLeftHeaderSection,
  StyledDarkSection,
  StyledDetailsContainer,
  StyledSidebarContainer,
  StyledMainContainer,
  StyledPostTitle,
  StyledSectionHeading,
  StyledParagraph,
} from '../Blog/styled';
import { ReactComponent as LOGO } from '../../assets/img/tol-logo.svg';
import { ReactComponent as LOGOMobile } from '../../assets/img/tol-logo-mobile.svg';
import Seo from '../../components/Seo';
import { PAGE_SEO } from '../../service/seoMeta';
import { accountDeletionRequest } from '../../service/links';
import { PRIVACY_CONTACT_EMAIL } from '../../service/accountDeletion';
import DeleteAccountForm from './DeleteAccountForm';
import { StyledList } from './styled';

export const DELETION_TIMING_TEXT =
  'We’ll contact you through the email or Discord account linked to your TOL account to confirm before deleting. We’ll complete deletion within 30 days.';

// Confirmed by Patrick (backend #167), verbatim. Keep in sync with what deletion actually does.
export const DELETION_SCOPE_TEXT =
  'When we delete your account, we remove your account and sign-in details. Your club, its squad and its in-game history stay in the league, run by a bot until a new manager takes over. Posts that already mention your username, such as league news, press articles and Discord messages, aren\'t removed automatically, and we keep purchase records we need for accounting.';

/**
 * Public account deletion request page (/delete-account). No login needed.
 * Google Play lists this URL as the app's account deletion link.
 */
export default function DeleteAccountPage({ endpoint = accountDeletionRequest }) {
  return (
    <StyledPageWrapper>
      <Seo {...PAGE_SEO.deleteAccount} />
      <StyledDarkSection>
        <StyledHeader>
          <StyledLeftHeaderSection>
            <Link to="/" aria-label="The Open League home">
              {isMobile ? <LOGOMobile /> : <LOGO />}
            </Link>
          </StyledLeftHeaderSection>
        </StyledHeader>
      </StyledDarkSection>
      <StyledDetailsContainer>
        <StyledSidebarContainer />
        <StyledMainContainer>
          <StyledPostTitle>Delete your account</StyledPostTitle>
          <StyledParagraph big>
            Use this page to ask us to delete your The Open League account. You
            don’t need to log in, and this works for accounts made on the
            website and in the mobile apps.
          </StyledParagraph>

          <StyledSectionHeading as="h2">What we delete</StyledSectionHeading>
          <StyledList>
            <li>
              Your account: your username, the email address on your account,
              and the links to the Discord, Google or Apple account you log in
              with.
            </li>
            <li>
              Discord details we keep for login, such as your Discord avatar.
            </li>
            <li>
              Device and app data: the push notification token for your phone
              and the IP address we stored for your account.
            </li>
            <li>
              Your manager profile: your onboarding answers, trophy counts,
              Patron status and any feedback you sent us.
            </li>
          </StyledList>
          <StyledParagraph small data-testid="delete-account-scope">
            {DELETION_SCOPE_TEXT}
          </StyledParagraph>

          <StyledSectionHeading as="h2">How long it takes</StyledSectionHeading>
          <StyledParagraph small>{DELETION_TIMING_TEXT}</StyledParagraph>

          <StyledSectionHeading as="h2">What we may keep</StyledSectionHeading>
          <StyledParagraph small>
            We may keep some information for longer where the law requires or
            permits it, for example for tax or accounting reasons. If a copy is
            in a backup archive, we store it securely and keep it away from any
            further use until it can be deleted.
          </StyledParagraph>

          <StyledSectionHeading as="h2">Request deletion</StyledSectionHeading>
          <DeleteAccountForm endpoint={endpoint} />

          <StyledParagraph small>
            Questions? Email{' '}
            <a href={`mailto:${PRIVACY_CONTACT_EMAIL}`}>{PRIVACY_CONTACT_EMAIL}</a>{' '}
            or read our <Link to="/privacy">privacy policy</Link>.
          </StyledParagraph>
        </StyledMainContainer>
      </StyledDetailsContainer>
    </StyledPageWrapper>
  );
}
